import { useEffect, useRef } from 'react'
import { patchBrandKit } from '../../creative/api/brand-kit-api'
import { useBrandKitStore } from '../../creative/store/brand-kit-store'
import { getBrowserTimezone } from '../utils/business-timezone'

/**
 * Lazy-capture the brand timezone the first time the user opens a surface
 * that depends on it (calendar, post modal).
 *
 * Why lazy instead of capturing at onboarding: the geolocation service isn't
 * wired into business creation, and IP-based detection is less accurate than
 * the browser's own `Intl.DateTimeFormat().resolvedOptions().timeZone` —
 * which already knows about the user's OS-level TZ + DST.
 *
 * Guarantees:
 *  - Fires at most ONE PATCH per session (idempotent via `firedRef`).
 *  - No-ops once `brandKit.timezone` is non-null (the BrandKit page editor or
 *    a prior capture has already set it).
 *  - Optimistically writes the captured value into the local store so the
 *    same render that triggered the capture already sees the right value.
 *  - Silent on failure — the user never sees an error toast for this. The
 *    next visit will just retry.
 */
export function useEnsureBrandKitTimezone(): void {
  const timezone = useBrandKitStore(s => s.brandKit.timezone)
  const updateBrandKit = useBrandKitStore(s => s.updateBrandKit)
  const firedRef = useRef(false)

  useEffect(() => {
    if (timezone) return
    if (firedRef.current) return
    const detected = getBrowserTimezone()
    if (!detected) return
    firedRef.current = true
    // Optimistic local update so consumers in this render don't fall back to
    // UTC for one extra frame. The server PATCH is fire-and-forget — if it
    // fails the local store still has the right value for the session and the
    // next visit will reissue the PATCH.
    updateBrandKit({ timezone: detected })
    void patchBrandKit({ timezone: detected }).catch(err => {
      console.warn('[useEnsureBrandKitTimezone] PATCH failed:', err)
    })
  }, [timezone, updateBrandKit])
}
